import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  FileSearch, TrendingUp, Users, Shield, AlertTriangle, Activity,
  School, Loader2, RefreshCw, Eye, Clock, Monitor, Smartphone, Globe,
  BarChart3, PieChart as PieChartIcon, Zap, ArrowUpRight, ArrowDownRight,
  MousePointerClick, Server, Lock, MapPin, CalendarDays, Hash
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { adminApi } from '@/services/api'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts'

const SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const YEAR_ID_KEY = 'lybibal-selected-year-id'
const YEAR_LABEL_KEY = 'lybibal-selected-year-label'

const COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316']
const GRADIENTS = [
  'from-sky-500 to-blue-600',
  'from-emerald-500 to-green-600',
  'from-amber-500 to-orange-600',
  'from-red-500 to-rose-600',
  'from-violet-500 to-purple-600',
  'from-pink-500 to-rose-500',
]

interface AuditStats {
  total_logs: number
  today_logs: number
  login_count: number
  error_count: number
  warning_count: number
  critical_count: number
  unique_users: number
  action_breakdown: Record<string, number>
  severity_breakdown: Record<string, number>
  top_actions: Array<{ action: string; count: number }>
  top_users: Array<{ user_id: number; user_name: string; count: number }>
  period_days: number
}

interface AnalyticsData {
  total_visits_today: number
  total_visits_week: number
  total_visits_month: number
  unique_visitors_today: number
  unique_visitors_week: number
  avg_duration_ms: number
  avg_duration_seconds: number
  top_pages: Array<{
    page_path: string
    visit_count: number
    unique_visitors: number
    priority_score: number
  }>
  device_breakdown: Record<string, number>
  browser_breakdown: Record<string, number>
  os_breakdown: Record<string, number>
  category_breakdown: Record<string, number>
  visits_by_hour: Array<{ hour: number; count: number }>
  visits_by_day: Array<{ day: string; count: number; full_date: string }>
}

export default function AuditDashboardPage() {
  const { lang } = useLang()
  const [loading, setLoading] = useState(true)
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null)
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())
  const [period, setPeriod] = useState<7 | 30 | 90>(30)

  const selectedSchoolId = useMemo(() => {
    const stored = localStorage.getItem(SCHOOL_ID_KEY)
    if (stored) { const val = Number(stored); if (!isNaN(val) && val > 0) return val }
    const auth = localStorage.getItem('auth-storage')
    if (auth) { try { const p = JSON.parse(auth); const sid = p?.state?.user?.school_id; if (sid) return Number(sid) } catch {} }
    return null
  }, [])

  const selectedAcademicYearId = useMemo(() => {
    const stored = localStorage.getItem(YEAR_ID_KEY)
    if (stored) { const val = Number(stored); if (!isNaN(val) && val > 0) return val }
    const auth = localStorage.getItem('auth-storage')
    if (auth) { try { const p = JSON.parse(auth); const yid = p?.state?.user?.academic_year_id; if (yid) return Number(yid) } catch {} }
    return null
  }, [])

  const selectedYearLabel = useMemo(() => localStorage.getItem(YEAR_LABEL_KEY) || '', [])

  const loadData = useCallback(async () => {
    if (!selectedSchoolId || !selectedAcademicYearId) { setLoading(false); return }
    setLoading(true)
    try {
      const [statsRes, analyticsRes] = await Promise.all([
        adminApi.getAuditStatistics(period),
        adminApi.getAnalyticsDashboard(),
      ])
      setAuditStats(statsRes)
      setAnalytics(analyticsRes)
      setLastRefresh(new Date())
    } catch (err) {
      console.error('Error loading dashboard:', err)
      toast.error(lang === 'fr' ? 'Erreur chargement dashboard' : 'Error loading dashboard')
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId, selectedAcademicYearId, period, lang])

  useEffect(() => { loadData() }, [loadData])
  useEffect(() => {
    const interval = setInterval(loadData, 30000)
    return () => clearInterval(interval)
  }, [loadData])

  const $t = useMemo(() => ({
    fr: {
      title: 'Tableau de bord Audit & Analytics',
      subtitle: 'Sécurité, traçabilité et performance en temps réel',
      totalLogs: 'Logs total', todayLogs: "Aujourd'hui", logins: 'Connexions',
      errors: 'Erreurs', warnings: 'Avertissements', critical: 'Critiques',
      uniqueUsers: 'Utilisateurs uniques', visitsToday: "Visites aujourd'hui",
      visitsWeek: 'Visites semaine', visitsMonth: 'Visites mois',
      avgDuration: 'Durée moyenne', topPages: 'Pages populaires',
      devices: 'Appareils', browsers: 'Navigateurs', os: 'Systèmes',
      categories: 'Catégories', trafficTrend: 'Tendance du trafic',
      hourlyTraffic: 'Trafic horaire', security: 'Sécurité',
      topActions: 'Actions fréquentes', activeUsers: 'Utilisateurs actifs',
      lastRefresh: 'Actualisé à', refresh: 'Actualiser',
      period: 'Période', days7: '7 jours', days30: '30 jours', days90: '90 jours',
      noData: 'Aucune donnée disponible', sessions: 'Sessions',
      page: 'page', visits: 'visites', ofTotal: 'sur le total',
    },
    en: {
      title: 'Audit & Analytics Dashboard',
      subtitle: 'Security, traceability and real-time performance',
      totalLogs: 'Total logs', todayLogs: 'Today', logins: 'Logins',
      errors: 'Errors', warnings: 'Warnings', critical: 'Critical',
      uniqueUsers: 'Unique users', visitsToday: 'Visits today',
      visitsWeek: 'Week visits', visitsMonth: 'Month visits',
      avgDuration: 'Avg duration', topPages: 'Top pages',
      devices: 'Devices', browsers: 'Browsers', os: 'Operating systems',
      categories: 'Categories', trafficTrend: 'Traffic trend',
      hourlyTraffic: 'Hourly traffic', security: 'Security',
      topActions: 'Top actions', activeUsers: 'Active users',
      lastRefresh: 'Updated at', refresh: 'Refresh',
      period: 'Period', days7: '7 days', days30: '30 days', days90: '90 days',
      noData: 'No data available', sessions: 'Sessions',
      page: 'page', visits: 'visits', ofTotal: 'of total',
    }
  }), [])[lang]

  const kpiCards = useMemo(() => {
    if (!auditStats || !analytics) return []
    return [
      {
        label: $t.visitsToday, value: analytics.total_visits_today,
        sub: `${analytics.unique_visitors_today} ${$t.sessions.toLowerCase()}`,
        icon: MousePointerClick, color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-100',
        gradient: 'from-sky-500 to-blue-600', trend: 'up'
      },
      {
        label: $t.totalLogs, value: auditStats.total_logs,
        sub: `+${auditStats.today_logs} ${$t.todayLogs.toLowerCase()}`,
        icon: FileSearch, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100',
        gradient: 'from-emerald-500 to-green-600', trend: 'up'
      },
      {
        label: $t.errors, value: auditStats.error_count + auditStats.critical_count,
        sub: `${auditStats.warning_count} ${$t.warnings.toLowerCase()}`,
        icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100',
        gradient: 'from-red-500 to-rose-600', trend: 'down'
      },
      {
        label: $t.uniqueUsers, value: auditStats.unique_users,
        sub: `${analytics.unique_visitors_week || 0} ${$t.sessions.toLowerCase()} / 7j`,
        icon: Users, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100',
        gradient: 'from-violet-500 to-purple-600', trend: 'up'
      },
    ]
  }, [auditStats, analytics, $t])

  const secondaryCards = useMemo(() => {
    if (!analytics) return []
    return [
      {
        label: $t.visitsWeek, value: analytics.total_visits_week,
        sub: `~${Math.round(analytics.total_visits_week / 7)}/jour`,
        icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50'
      },
      {
        label: $t.visitsMonth, value: analytics.total_visits_month,
        sub: `~${Math.round(analytics.total_visits_month / 30)}/jour`,
        icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-50'
      },
      {
        label: $t.avgDuration, value: `${analytics.avg_duration_seconds}s`,
        sub: `${analytics.avg_duration_ms}ms`,
        icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50'
      },
      {
        label: $t.logins, value: auditStats?.login_count || 0,
        sub: auditStats?.period_days ? `${$t.period}: ${auditStats.period_days}j` : '',
        icon: Lock, color: 'text-teal-600', bg: 'bg-teal-50'
      },
    ]
  }, [analytics, auditStats, $t])

  const deviceChartData = useMemo(() => {
    if (!analytics?.device_breakdown) return []
    const total = Object.values(analytics.device_breakdown).reduce((a, b) => a + b, 0) || 1
    return Object.entries(analytics.device_breakdown).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      percent: Math.round((value / total) * 100)
    }))
  }, [analytics])

  const browserChartData = useMemo(() => {
    if (!analytics?.browser_breakdown) return []
    const total = Object.values(analytics.browser_breakdown).reduce((a, b) => a + b, 0) || 1
    return Object.entries(analytics.browser_breakdown).map(([name, value]) => ({
      name,
      value,
      percent: Math.round((value / total) * 100)
    }))
  }, [analytics])

  const categoryChartData = useMemo(() => {
    if (!analytics?.category_breakdown) return []
    return Object.entries(analytics.category_breakdown)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [analytics])

  const actionChartData = useMemo(() => {
    if (!auditStats?.top_actions) return []
    const labels: Record<string, string> = {
      CREATE: 'Création', UPDATE: 'Modification', DELETE: 'Suppression',
      LOGIN: 'Connexion', LOGOUT: 'Déconnexion', EXPORT: 'Export',
      IMPORT: 'Import', PRINT: 'Impression', APPROVE: 'Approbation',
      REJECT: 'Rejet', VIEW: 'Consultation', ACCESS_DENIED: 'Accès refusé',
    }
    return auditStats.top_actions.slice(0, 8).map(a => ({
      name: labels[a.action] || a.action,
      value: a.count,
    }))
  }, [auditStats])

  if (!selectedSchoolId || !selectedAcademicYearId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">
            {lang === 'fr' ? 'Aucun établissement ou année scolaire sélectionné' : 'No school or academic year selected'}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {lang === 'fr' ? 'Veuillez sélectionner une année scolaire dans le header' : 'Please select an academic year in the header'}
          </p>
        </div>
      </div>
    )
  }

  if (loading && !auditStats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-sky-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">{$t.noData}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/30 to-blue-50/20">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-sky-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-200">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-700 to-blue-900 bg-clip-text text-transparent">
                  {$t.title}
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">{$t.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {selectedYearLabel && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-sky-100 text-sky-700 border border-sky-200">
                  <School className="w-3 h-3 mr-1.5" />{selectedYearLabel}
                </span>
              )}
              {/* Period selector */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {([7, 30, 90] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setPeriod(d)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      period === d ? 'bg-white text-sky-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {d === 7 ? $t.days7 : d === 30 ? $t.days30 : $t.days90}
                  </button>
                ))}
              </div>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {lastRefresh.toLocaleTimeString(lang === 'fr' ? 'fr-FR' : 'en-US')}
              </span>
              <button
                onClick={loadData}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 bg-sky-50 text-sky-700 rounded-lg text-sm font-medium hover:bg-sky-100 transition-colors border border-sky-200 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {$t.refresh}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* ═══ PRIMARY KPI CARDS ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((card, idx) => {
            const Icon = card.icon
            return (
              <div
                key={idx}
                className={`relative bg-white rounded-2xl p-5 border ${card.border} shadow-sm hover:shadow-lg transition-all duration-300 group overflow-hidden`}
              >
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${card.gradient} opacity-5 rounded-bl-full group-hover:opacity-10 transition-opacity`} />
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 ${card.bg} rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${card.color}`} />
                  </div>
                  {card.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <p className="text-2xl font-bold text-gray-900">{card.value.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">{card.label}</p>
                <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
              </div>
            )
          })}
        </div>

        {/* ═══ SECONDARY CARDS ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {secondaryCards.map((card, idx) => {
            const Icon = card.icon
            return (
              <div key={idx} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${card.bg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${card.color}`} />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-800">{typeof card.value === 'number' ? card.value.toLocaleString() : card.value}</p>
                    <p className="text-xs text-gray-500">{card.label}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">{card.sub}</p>
              </div>
            )
          })}
        </div>

        {/* ═══ CHARTS ROW 1: Traffic Trend + Hourly ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Traffic Trend (7 days) */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-sky-600" />
                <h2 className="text-lg font-bold text-gray-800">{$t.trafficTrend}</h2>
              </div>
              <span className="text-xs text-gray-400">7 {$t.days7.replace(/\d+ /, '')}</span>
            </div>
            <div className="p-5">
              {analytics?.visits_by_day && analytics.visits_by_day.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={analytics.visits_by_day}>
                    <defs>
                      <linearGradient id="trafficGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                      formatter={(value: number) => [value, $t.visits]}
                    />
                    <Area type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} fill="url(#trafficGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex flex-col items-center justify-center text-gray-400">
                  <TrendingUp className="w-12 h-12 mb-2" />
                  <p>{$t.noData}</p>
                </div>
              )}
            </div>
          </div>

          {/* Hourly Traffic */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-2">
              <Clock className="w-5 h-5 text-sky-600" />
              <h2 className="text-lg font-bold text-gray-800">{$t.hourlyTraffic}</h2>
            </div>
            <div className="p-5">
              {analytics?.visits_by_hour && analytics.visits_by_hour.some(d => d.count > 0) ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.visits_by_hour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(h) => `${h}h`} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                      formatter={(value: number) => [value, $t.visits]}
                      labelFormatter={(label) => `${label}h`}
                    />
                    <Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex flex-col items-center justify-center text-gray-400">
                  <Clock className="w-12 h-12 mb-2" />
                  <p>{$t.noData}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══ CHARTS ROW 2: Devices + Browsers + Categories ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Device Breakdown */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-sky-600" />
              <h2 className="text-lg font-bold text-gray-800">{$t.devices}</h2>
            </div>
            <div className="p-5">
              {deviceChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={deviceChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {deviceChartData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                  <Monitor className="w-10 h-10 mb-2" />
                  <p className="text-sm">{$t.noData}</p>
                </div>
              )}
            </div>
          </div>

          {/* Browser Breakdown */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-2">
              <Globe className="w-5 h-5 text-sky-600" />
              <h2 className="text-lg font-bold text-gray-800">{$t.browsers}</h2>
            </div>
            <div className="p-5">
              {browserChartData.length > 0 ? (
                <div className="space-y-3">
                  {browserChartData.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="text-sm text-gray-700 w-20">{item.name}</span>
                      <div className="flex-1">
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ width: `${item.percent}%`, backgroundColor: COLORS[idx % COLORS.length] }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">{item.value}</span>
                      <span className="text-xs text-gray-400 w-10 text-right">{item.percent}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                  <Globe className="w-10 h-10 mb-2" />
                  <p className="text-sm">{$t.noData}</p>
                </div>
              )}
            </div>
          </div>

          {/* Page Categories */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-sky-600" />
              <h2 className="text-lg font-bold text-gray-800">{$t.categories}</h2>
            </div>
            <div className="p-5">
              {categoryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={categoryChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name }) => name}
                      labelLine={false}
                    >
                      {categoryChartData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                  <PieChartIcon className="w-10 h-10 mb-2" />
                  <p className="text-sm">{$t.noData}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══ ROW 3: Top Pages + Top Actions ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Pages */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-2">
              <Eye className="w-5 h-5 text-sky-600" />
              <h2 className="text-lg font-bold text-gray-800">{$t.topPages}</h2>
            </div>
            <div className="p-5">
              {analytics?.top_pages && analytics.top_pages.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_pages.slice(0, 8).map((page, idx) => {
                    const maxVisits = analytics.top_pages[0]?.visit_count || 1
                    const pct = Math.min(100, (page.visit_count / maxVisits) * 100)
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0 bg-gradient-to-br ${GRADIENTS[idx % GRADIENTS.length]}`}>
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 font-medium truncate" title={page.page_path}>{page.page_path}</span>
                            <span className="font-semibold text-gray-900">{page.visit_count}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className="h-2 rounded-full bg-sky-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="py-12 text-center text-gray-400">
                  <Eye className="w-12 h-12 mx-auto mb-2" />
                  <p>{$t.noData}</p>
                </div>
              )}
            </div>
          </div>

          {/* Top Actions */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-2">
              <Zap className="w-5 h-5 text-sky-600" />
              <h2 className="text-lg font-bold text-gray-800">{$t.topActions}</h2>
            </div>
            <div className="p-5">
              {actionChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={actionChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#64748b' }} width={100} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                      formatter={(value: number) => [value, 'Count']}
                    />
                    <Bar dataKey="value" fill="#0ea5e9" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex flex-col items-center justify-center text-gray-400">
                  <Zap className="w-12 h-12 mb-2" />
                  <p>{$t.noData}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══ ROW 4: Security Summary ═══ */}
        {auditStats && (auditStats.error_count > 0 || auditStats.critical_count > 0 || auditStats.warning_count > 0) && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border border-red-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-red-100 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-bold text-red-800">{$t.security}</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">{auditStats.error_count}</p>
                  <p className="text-sm text-red-700 mt-1">{$t.errors}</p>
                </div>
                <div className="text-center border-x border-red-100">
                  <p className="text-3xl font-bold text-orange-600">{auditStats.warning_count}</p>
                  <p className="text-sm text-orange-700 mt-1">{$t.warnings}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600">{auditStats.critical_count}</p>
                  <p className="text-sm text-purple-700 mt-1">{$t.critical}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ ROW 5: Active Users List ═══ */}
        {auditStats?.top_users && auditStats.top_users.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-600" />
              <h2 className="text-lg font-bold text-gray-800">{$t.activeUsers}</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {auditStats.top_users.slice(0, 10).map((u, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-sky-50 transition-colors">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br ${GRADIENTS[idx % GRADIENTS.length]}`}>
                      {u.user_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{u.user_name || `User ${u.user_id}`}</p>
                      <p className="text-xs text-gray-500">{u.count} actions</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}